import { Typography, Box, Icon, Button, Select, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { formatDateToJalali, numberToTomans } from '../../../utils/helpers';
import Badge from '../../base/BaseBadge/BaseBadge';
import BaseDialog from '../../base/BaseDialog/BaseDialog';
import { useState } from 'react';
import { BaseButton, BaseInput } from '../../base';
import { useNotification } from '../../../context/NotificationProvider';
import useProtectedClient from '../../../hooks/useProtectedClient';
import { changeCustomerInfo } from '../../../api/admin/adminUserApi';

const AdminUserItem = ({ item }) => {
    const theme= useTheme();
    const [isLoading, setIsLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedType, setSelectedType] = useState(item?.customerType)
    const [displayName, setDisplayName] = useState(item?.profile.displayName)
    const {showNotification} = useNotification();
    const protectedClient = useProtectedClient();
    const handleDialogOpen = () => {
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    const handleConfirm = async () => {
        setIsLoading(true);
        try {
            await changeCustomerInfo(protectedClient, item._id, selectedType, displayName)
            showNotification("با موفقیت به روز شد", "success");
        } catch (error) {
            showNotification(error, "error");
        } finally {
            setDialogOpen(false);
            setIsLoading(false);
            window.location.reload();
        }
    };

    const getAuthBadgeProps = (authStatus) => { 
        switch (authStatus) {
            case 0:
                return { type: "error", text: "احراز نشده" };
            case 1:
                return { type: "warning", text: "تایید شماره"  };
            case 2:
                return { type: "warning", text: "تایید پسورد" };
            case 3:
                return { type: "success", text: "تایید کد ملی" };
            default:
                return { type: "error", text: "" };
        }
    };

    const getTypeBadgeProps = (customerType) => { 
        switch (customerType) {
            case 0:
                return { type: "info", text: "کاربر" };
            case 1:
                return { type: "info", text: "عاملیت فروش" };
            case 2:
                return { type: "info", text: "همکار" };
            case 3:
                return { type: "info", text: "VIP" };
            case 4:
                return { type: "info", text: "CIP" };
            case 5:
                return { type: "info", text: "پرسنل زربد" };
            default:
                return { type: "error", text: "" };
        }
    };

    const handleTypeChange = (event) => {
        setSelectedType(event.target.value);
    };
    const authBadgeProps = getAuthBadgeProps(item?.authStatus);
    const typeBadgeProps = getTypeBadgeProps(item?.customerType);
    const dialogRows = [
        {
            title: "نام و نام خانوادگی:",
            value: `${item?.profile.firstName} ${item?.profile.lastName}`
        },
        {
            title: "نام مشتری:",
            value: (
                <BaseInput
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    name="displayName"
                    type="text"
                />
            )
        },
        {
            title: "شماره تماس:",
            value: `${item?.profile.phone}`
        },
        {
            title: "نوع مشتری:",
            value: (
                <Select
                    value={selectedType}
                    onChange={handleTypeChange}
                    variant="outlined"
                    size="small"
                >
                    <MenuItem value={0}> کاربر </MenuItem>
                    <MenuItem value={1}> عاملیت فروش </MenuItem>
                    <MenuItem value={2}> همکار </MenuItem>
                    <MenuItem value={3}> VIP </MenuItem>
                    <MenuItem value={4}> CIP </MenuItem>
                    <MenuItem value={5}> پرسنل زربد </MenuItem>
                </Select>
            )
        },
        {
            title: "کد ملی:",
            value: `${item?.profile.nationalId}`
        },
        {
            title: "کد دعوت",
            value: `${item?.referCode}`
        },
        {
            title: "احراز هویت",
            value: <Badge {...authBadgeProps} />
        },
        {
            title: "موجودی کیف پول:",
            value: `${numberToTomans(item?.walletBallance)} تومان`
        },
        {
            title: "موجودی طلا",
            value: `${numberToTomans(item?.goldBallance)} گرم`
        }
    ];
    return (
        <>
            <BaseDialog
                isOpen={dialogOpen}
                handleClose={handleDialogClose}
                title="جزییات کاربر"
            >
                {dialogRows.map((row, index) => (
                    <Box
                        key={index}
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                            width: '100%',
                            padding: '10px'
                        }}
                    >
                        <Typography
                            variant='caption2'
                        >
                            {row.title}
                        </Typography>
                        <Typography
                            variant='caption2'
                        >
                            {row.value}
                        </Typography>
                    </Box>
                ))}
                <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        width: '100%',
                        padding: '10px'
                    }}
                >
                    <BaseButton
                        onClick={() => handleConfirm()}
                        isLoading={isLoading}
                        sx={{
                            margin: "10px",
                            backgroundColor: theme.palette.success.main,
                            color: theme.palette.text.secondary
                        }}
                    >
                        بروزرسانی
                    </BaseButton>
                </Box>
            </BaseDialog>
            <Button
                onClick={() => handleDialogOpen(item)}
                sx={{ 
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: 'center',
                    width: "100%",
                    margin: "5px 0",
                    padding: "10px",
                    borderRadius: "10px",
                    border: "1px solid"
                }}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-start"
                    alignItems="center"
                    gap={2}
                    sx={{ width: "10%", flexGrow: 1 }}
                >
                    <Icon
                        sx={{
                            display: 'flex',
                            fontSize: 24,
                            color: theme.palette.background.gray,
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <AccountCircleIcon/>
                    </Icon>
                </Box>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="flex-start"
                    gap={1}
                    sx={{ width: "60%", flexGrow: 1 }}
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Typography
                            variant='subtitle'
                        >
                            {`${item?.profile.displayName} ` }
                        </Typography>
                    </Box>
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Typography
                            variant='subtitle2'
                        >
                            {`${item?.profile.phone}` }
                        </Typography>
                    </Box>
                </Box>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-start"
                    alignItems="center"
                    gap={1}
                    sx={{ width: "30%", flexGrow: 1 }}
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        gap={2}
                    >
                        <Badge {...typeBadgeProps} />
                    </Box>
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        gap={2}
                    >
                        <Badge {...authBadgeProps} />
                    </Box>
                </Box>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-start"
                    alignItems="center"
                    gap={2}
                    sx={{ width: "5%", flexGrow: 1 }}
                >
                    <Icon
                        sx={{
                            display: 'flex',
                            fontSize: 24,
                            color: theme.palette.background.gray,
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <ChevronLeftIcon/>
                    </Icon>
                </Box>
            </Button>
        </>
    );
};

export default AdminUserItem;
